import type { ReactNode } from 'react'
import { Hourglass, Layers as LayersIcon, Maximize2 } from 'lucide-react'
import { IconButton, Sheet, cx } from '@/components/ui'

// The graph's controls on a phone (/agents/graph below the md breakpoint): one
// strip under the canvas, where the thumb rests, instead of chips along the top.

export interface GraphModeItem {
  key: string
  label: string
}
export interface GraphLayerItem {
  key: string
  label: string
  on: boolean
  /** part of the current view: shown, not switchable */
  needed: boolean
}

/**
 * Views as a scrolling row of pills, then What is waiting (when the factory is
 * on), Fit and Layers; every target at least 44px.
 */
export const GraphPhoneBar = ({
  modes,
  mode,
  onMode,
  onFit,
  onLayers,
  onWaiting,
  onYou = false,
}: {
  modes: GraphModeItem[]
  mode: string
  onMode: (key: string) => void
  onFit: () => void
  onLayers: () => void
  /** opens the waiting board's sheet; no button without it */
  onWaiting?: () => void
  /** something waits on the owner: the button carries a lit dot (never a count) */
  onYou?: boolean
}) => (
  <div className="flex items-center gap-1 pt-2 shrink-0" data-testid="graph-phone-bar">
    <div
      // the views scroll on their own, fading at the edge so a cut one reads as more to come
      className="flex-1 min-w-0 flex items-center gap-1 overflow-x-auto [mask-image:linear-gradient(to_right,black_85%,transparent)] pr-6"
      role="group"
      aria-label="View"
      data-testid="graph-modes"
    >
      {modes.map((m) => (
        <button
          key={m.key}
          type="button"
          aria-pressed={mode === m.key}
          onClick={() => onMode(m.key)}
          className={cx(
            'shrink-0 rounded-full border px-3 min-h-11 text-[13px] whitespace-nowrap transition-colors',
            mode === m.key
              ? 'border-line-strong bg-raised text-fg font-semibold'
              : 'border-transparent text-muted',
          )}
          data-testid={`graph-mode-${m.key}`}
        >
          {m.label}
        </button>
      ))}
    </div>
    {onWaiting && (
      <IconButton
        label="What is waiting"
        onClick={onWaiting}
        className="relative min-h-11 min-w-11"
        data-testid="graph-waiting-open"
      >
        <Hourglass size={18} aria-hidden />
        {onYou && <WaitingDot />}
      </IconButton>
    )}
    <IconButton label="Fit" onClick={onFit} className="min-h-11 min-w-11">
      <Maximize2 size={18} aria-hidden />
    </IconButton>
    <IconButton
      label="Layers"
      onClick={onLayers}
      className="min-h-11 min-w-11"
      data-testid="graph-layers-open"
    >
      <LayersIcon size={18} aria-hidden />
    </IconButton>
  </div>
)

/** The lit dot on a waiting button: something is on you. */
export const WaitingDot = ({ inline = false }: { inline?: boolean }) => (
  <span
    className={cx('size-2 rounded-full bg-accent shrink-0', !inline && 'absolute top-2 right-2')}
    data-testid="graph-waiting-dot"
    aria-hidden
  />
)

/** Layers, "ended this week" and the legend, in a bottom sheet. */
export const GraphLayersSheet = ({
  open,
  onClose,
  layers,
  onToggle,
  week,
  onWeek,
  children,
}: {
  open: boolean
  onClose: () => void
  layers: GraphLayerItem[]
  onToggle: (key: string) => void
  week: boolean
  onWeek: (on: boolean) => void
  /** the legend */
  children?: ReactNode
}) => (
  <Sheet open={open} onClose={onClose} title="Layers">
    <div className="flex flex-col" role="group" aria-label="Layers">
      {layers.map((l) => (
        <label
          key={l.key}
          className={cx(
            'flex items-center justify-between gap-3 min-h-11 border-b border-line text-[15px]',
            l.needed && 'opacity-70',
          )}
        >
          <span>{l.label}</span>
          <input
            type="checkbox"
            className="h-5 w-5"
            checked={l.on}
            disabled={l.needed}
            onChange={() => onToggle(l.key)}
            data-testid={`graph-layer-${l.key}`}
          />
        </label>
      ))}
      <label className="flex items-center justify-between gap-3 min-h-11 border-b border-line text-[15px]">
        <span>Ended this week</span>
        <input
          type="checkbox"
          className="h-5 w-5"
          checked={week}
          onChange={(e) => onWeek(e.target.checked)}
          data-testid="graph-week"
        />
      </label>
    </div>
    {children && <div className="pt-3">{children}</div>}
  </Sheet>
)
